// Formatting helpers. All money is formatted in the *destination* locale, because a Swiss
// buyer expects CHF 286'000 and a French one 286 000 €.

const LOCALES = { CH: "de-CH", FR: "fr-FR", DE: "de-DE", LU: "fr-LU" };
const LANG_LOCALES = { fr: "fr-FR", de: "de-DE", en: "en-GB", it: "it-IT" };

export function money(amount, currency, country = "CH", { decimals = 0 } = {}) {
  if (amount == null || Number.isNaN(amount)) return "—";
  return new Intl.NumberFormat(LOCALES[country] || "de-CH", {
    style: "currency", currency: currency || "EUR",
    maximumFractionDigits: decimals, minimumFractionDigits: decimals,
  }).format(amount).replaceAll('’', "'");
}

/** A band as "286,000" or, when the spread is wide enough to matter, "277,000 – 307,000". */
export function moneyBand(band, currency, country = "CH", { alwaysRange = false } = {}) {
  if (!band) return "—";
  const spread = band.max - band.min;
  const rel = band.typical ? spread / band.typical : 0;
  if (!alwaysRange && rel < 0.02) return money(band.typical, currency, country);
  return `${money(band.min, currency, country)} – ${money(band.max, currency, country)}`;
}

export function num(n, lang = "en") {
  if (n == null || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat(LANG_LOCALES[lang] || "en-GB").format(n);
}

export const km = (n, lang = "en") => (n == null ? "—" : `${num(n, lang)} km`);

export function date(iso, lang = "en") {
  if (!iso) return "—";
  return new Intl.DateTimeFormat(LANG_LOCALES[lang] || "en-GB", { day: "numeric", month: "short", year: "numeric" }).format(new Date(iso));
}

/**
 * A clean display title.
 *
 * Sources publish names in their own house style and it shows: Goo-net returns
 * "FERRARI / SF90 STRADALE / BASE GRADE" — 323 of its 387 listings are all-caps, and 75
 * carry "BASE GRADE", which is a database default, not a trim. Rendered raw that reads as
 * shouting placeholder text on a EUR 700,000 car.
 *
 * Cleaning happens here, at the display layer, so `model` and `grade` keep exactly what the
 * source said (search, filters and the desk still see the original).
 */

// Grade values that carry no information about the car.
const EMPTY_GRADE = /^(base(\s+grade)?|standard|grade|no\s+grade|-)$/i;

// Encar writes the fuel into the grade — "Genesis G80 Gasoline 3.5 Turbo AWD" — and the
// card already prints Petrol as a spec directly underneath. Only the petrol spelling is
// dropped: "Hybrid" and "Diesel" distinguish one version of a model from another, but no
// manufacturer sells a car whose name is Gasoline.
const NOISE_IN_GRADE = /\b(gasoline|gasolin)\b/gi;

/**
 * Title-case a word, leaving model codes and acronyms alone.
 * "STRADALE" -> "Stradale", but "GTB", "F8", "296GTS", "SF90", "RS6" and "W205" stay put.
 */
function smartCase(word) {
  if (!word) return word;
  if (/[0-9]/.test(word)) return word;              // 296GTS, SF90, RS6, W205
  if (word.length <= 3) return word;                // GTB, GTS, AMG, S, F1
  if (word !== word.toUpperCase()) return word;     // already mixed case — leave it alone
  return word[0] + word.slice(1).toLowerCase();     // STRADALE -> Stradale
}

export function carTitle(l) {
  const raw = l.grade ? String(l.grade).replace(NOISE_IN_GRADE, " ").replace(/\s+/g, " ").trim() : "";
  const grade = raw && !EMPTY_GRADE.test(raw) ? raw : null;
  const parts = [l.make, l.model, grade].filter(Boolean);

  const out = [];
  for (const p of parts) {
    const norm = String(p).trim().replace(/\s+/g, " ");
    if (!norm) continue;
    if (out.some((o) => o.toLowerCase() === norm.toLowerCase())) continue;   // "Ferrari Ferrari"
    out.push(norm.split(" ").map(smartCase).join(" "));
  }

  // A model repeated inside the grade ("F430" + "F430 F1") reads badly; drop the repetition.
  const seen = new Set();
  const words = out.join(" ").split(" ").filter((w) => {
    const k = w.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  return words.join(" ") || l.title || "—";
}

const MARKET_LABEL = { encar: "market.encar", goonet: "market.goonet", jpauction: "market.jpauction" };
export const marketLabel = (source, t) => MARKET_LABEL[source] ? t(MARKET_LABEL[source]) : source;
